// Demo identity used ONLY by the explicit dev-demo mode (?demo=1 / localStorage
// command-center-demo=1). Production never auto-authenticates — sessions come
// from PocketBase OTP (fix #1; see App.jsx ProtectedShell).
export const demoUser = {
  id: 'usr_owner',
  name: 'Maya Chen',
  email: 'maya@northstar.example',
  role: 'owner'
};

export const initialState = {
  session: {
    user: null,
    authenticated: false
  },
  business: {
    id: 'biz_northstar',
    name: 'Northstar Studio',
    slug: 'northstar',
    industry: 'Sustainable home goods',
    audience: 'Design-conscious urban homeowners',
    goals: 'Grow qualified demand while preserving a thoughtful premium brand.',
    subscriptionPlanId: 'subscription_growth',
    aoUsedThisPeriod: 18,
    aoPeriodResetAt: '2025-09-01T00:00:00.000Z',
    theme: { colors: { gold: '#f3c969', mint: '#62c5a7' } }
  },
  brandProfile: {
    voice: 'Warm,precise,optimistic',
    avoid: 'Hype,jargon,aggressive urgency',
    principles: ['Useful before clever', 'Calm confidence', 'Specific proof']
  },
  modules: [
    { slug: 'introduction', name: 'Introduction', icon: 'Compass' },
    { slug: 'headquarters', name: 'Headquarters', icon: 'Grid' },
    { slug: 'website', name: 'Website', icon: 'Globe' },
    { slug: 'tasks', name: 'Tasks', icon: 'CheckSquare' },
    { slug: 'finance', name: 'Finance', icon: 'DollarSign' },
    { slug: 'ads', name: 'Ads', icon: 'Target' },
    { slug: 'inbox', name: 'Inbox', icon: 'Inbox' },
    { slug: 'files', name: 'Files', icon: 'Folder' },
    { slug: 'bots', name: 'Bots', icon: 'Cpu' },
    { slug: 'settings', name: 'Settings', icon: 'Settings' },
    { slug: 'models', name: 'Models', icon: 'Cpu' },
    { slug: 'marketplace', name: 'Marketplace', icon: 'ShoppingBag' },
    { slug: 'projects', name: 'Projects', icon: 'Clipboard' },
    { slug: 'calendar', name: 'Calendar', icon: 'Calendar' }
  ],
  modelTiers: [
    { id: 'tier_fast', name: 'Swift', speed: 'Very fast', cost: '$', description: 'Routine classification and drafting', applicableSections: ['inbox', 'tasks', 'chat_simple'] },
    { id: 'tier_balanced', name: 'Balanced', speed: 'Fast', cost: '$$', description: 'Reliable everyday business work', applicableSections: ['finance', 'ads', 'website', 'chat_simple'] },
    { id: 'tier_deep', name: 'Deep', speed: 'Deliberate', cost: '$$$', description: 'Complex strategy and synthesis', applicableSections: ['headquarters', 'finance', 'ads', 'tooling_advanced'] }
  ],
  teams: [
    { id: 'team_hq', slug: 'headquarters', name: 'Headquarters', module: 'headquarters' },
    { id: 'team_fin', slug: 'finance', name: 'Finance Team', module: 'finance' },
    { id: 'team_ads', slug: 'growth-team', name: 'Growth Team', module: 'ads' },
    { id: 'team_web', slug: 'website-team', name: 'Website Team', module: 'website' }
  ],
  bots: [
    { id: 'bot_bm', name: 'Avery', role: 'Business Manager', teamId: 'team_hq', color: '#111827', enabled: true, isLead: true, consultableBots: [], modelTier: 'tier_deep', agentClass: 'tooling_advanced', tools: ['file_retrieval'], systemPrompt: 'Coordinate the business and produce clear decisions.', knowledgeScope: 'Broad business strategy across all of this business modules: prioritization, cross-team coordination, and decision synthesis.', capabilities: ['cross-team-coordination','decision-support','priority-setting'] },
    { id: 'bot_finlead', name: 'Elena', role: 'Finance Lead', teamId: 'team_fin', color: '#5b8def', enabled: true, isLead: true, consultableBots: ['bot_analyst', 'bot_cash'], modelTier: 'tier_deep', agentClass: 'tooling_advanced', tools: ['file_retrieval'], systemPrompt: 'Lead financial analysis and consult specialists when needed.', knowledgeScope: 'Questions about this business financial figures: sales, pricing, forecasts, budgets, and decision-ready financial plans.', capabilities: ['financial-strategy','specialist-consultation','plan-creation'] },
    { id: 'bot_analyst', name: 'Noah', role: 'Revenue Analyst', teamId: 'team_fin', color: '#62c5a7', enabled: true, isLead: false, consultableBots: [], modelTier: 'tier_balanced', agentClass: 'chat_simple', tools: [], systemPrompt: 'Analyze revenue patterns and explain the drivers.', knowledgeScope: 'This business revenue figures: sales patterns, revenue drivers, and pricing performance behind the reported numbers.', capabilities: ['revenue-analysis','forecasting','variance-analysis'] },
    { id: 'bot_cash', name: 'Priya', role: 'Cash Flow Planner', teamId: 'team_fin', color: '#9979dc', enabled: true, isLead: false, consultableBots: [], modelTier: 'tier_balanced', agentClass: 'chat_simple', tools: [], systemPrompt: 'Model cash timing,runway,and practical tradeoffs.', knowledgeScope: 'This business cash position: inflows, outflows, runway, and the timing tradeoffs in its financial plans.', capabilities: ['cash-flow-modeling','runway-analysis','scenario-planning'] },
    { id: 'bot_growth', name: 'Mateo', role: 'Growth Lead', teamId: 'team_ads', color: '#ef806e', enabled: true, isLead: true, consultableBots: ['bot_copy'], modelTier: 'tier_deep', agentClass: 'tooling_advanced', tools: ['file_retrieval', 'web_search'], systemPrompt: 'Create evidence-led campaign strategies.', knowledgeScope: 'Questions about this business advertising: campaign strategy, audiences, budgets, channel mix, and measurable outcomes.', capabilities: ['campaign-strategy','brief-creation','specialist-consultation'] },
    { id: 'bot_copy', name: 'Zoe', role: 'Ad Copywriter', teamId: 'team_ads', color: '#f3c969', enabled: true, isLead: false, consultableBots: [], modelTier: 'tier_fast', agentClass: 'chat_simple', tools: [], systemPrompt: 'Write concise,distinctive ad concepts.', knowledgeScope: 'Ad copy and creative variants for this business campaigns: headlines, concepts, and brand-aligned messaging for approved briefs.', capabilities: ['ad-copy','creative-variants','brand-alignment'] },
    { id: 'bot_web', name: 'Iris', role: 'Web Strategist', teamId: 'team_web', color: '#5b8def', enabled: true, isLead: true, consultableBots: ['bot_seo'], modelTier: 'tier_deep', agentClass: 'tooling_advanced', tools: ['file_retrieval', 'web_search'], systemPrompt: 'Shape useful,conversion-aware websites.', knowledgeScope: 'Site content, structure, and conversion strategy for this business website: pages, copy direction, and visitor flow.', capabilities: ['site-strategy','content-structure','conversion-review'] },
    { id: 'bot_seo', name: 'Finn', role: 'SEO Specialist', teamId: 'team_web', color: '#62c5a7', enabled: true, isLead: false, consultableBots: [], modelTier: 'tier_balanced', agentClass: 'chat_simple', tools: [], systemPrompt: 'Improve discoverability without compromising clarity.', knowledgeScope: 'Search visibility for this business website: keywords, technical SEO, and on-page recommendations that support the site strategy.', capabilities: ['keyword-research','technical-seo','on-page-optimization'] }
  ],
  messages: {
    finance: [{ id: 'm1', sender: 'bot', botId: 'bot_finlead', text: 'I can help turn financial questions into a decision-ready plan.', created: new Date().toISOString() }],
    ads: [{ id: 'm2', sender: 'bot', botId: 'bot_growth', text: 'Tell me the offer and audience. I’ll shape a campaign brief.', created: new Date().toISOString() }],
    headquarters: [{ id: 'm3', sender: 'bot', botId: 'bot_bm', text: 'Good morning. What decision can I help coordinate?', created: new Date().toISOString() }],
    website: [
      {
        id: 'msg_web_hero',
        sender: 'bot',
        botId: 'bot_web',
        text: 'I drafted the new Hero Headline: “Handcrafted Goods Designed for Modern Living.” Finn confirmed the search volume is optimal for design-conscious homeowners.',
        producedBlockId: 'blk_hero',
        created: new Date().toISOString()
      },
      {
        id: 'msg_web_val',
        sender: 'bot',
        botId: 'bot_web',
        text: 'For the Value Proposition, I focused on artisanal provenance and material durability. This is currently awaiting your sign-off.',
        producedBlockId: 'blk_val',
        created: new Date().toISOString()
      }
    ]
  },
  plans: [
    { id: 'plan_1', title: 'Q3 Retention Push', status: 'approved', version: 2, module: 'ads', botId: 'bot_growth', project: 'proj_autumn_launch', outputContract: 'ad_campaign_brief', content: { fee: 4200, deliverables: ['Audience targeting configuration', '3 Creative ad variants', 'Conversion tracking setup'] } },
    { id: 'plan_2', title: 'Runway Protection Options', status: 'draft', version: 1, module: 'finance', botId: 'bot_finlead', project: 'proj_retail_expansion', outputContract: 'task', content: { fee: 2800, deliverables: ['Operating buffer model', 'Supplier payment schedule', 'Working capital forecast'] } }
  ],
  tasks: [
    { id: 't_demo_1', title: 'Publish Autumn Collection hero section', status: 'done', module: 'website', due: '2026-09-12', ownerType: 'bot', owner: 'Iris', planId: 'plan_1', project: 'proj_autumn_launch' },
    { id: 't_demo_2', title: 'Launch Q3 retention ad campaign', status: 'doing', module: 'ads', due: '2026-09-18', ownerType: 'bot', owner: 'Mateo', planId: 'plan_1', project: 'proj_autumn_launch' },
    { id: 't_demo_3', title: 'Finalize volume discount tiers with Sarah Jenkins', status: 'todo', module: 'finance', due: '2026-09-22', ownerType: 'human', owner: 'Maya', planId: 'plan_2', project: 'proj_retail_expansion' }
  ],
  files: [],
  inbox: [
    {
      id: 'inbox_1',
      from: 'sarah.jenkins@acmepartners.com',
      subject: 'Inquiry regarding wholesale volume pricing',
      preview: 'We are expanding our retail locations and would like to understand your minimum order thresholds for Q4…',
      body: 'Hi Northstar team,\n\nWe are looking to stock your sustainable ceramics across 6 of our regional retail stores ahead of the holiday season. Could you provide your wholesale tier schedule and estimated fulfillment lead times for orders over 500 units?\n\nBest regards,\nSarah Jenkins\nAcme Partners',
      status: 'needs-reply',
      triage_status: 'high_priority',
      draft: 'Hi Sarah,\n\nThank you for reaching out! We would be delighted to partner with Acme Partners. For orders exceeding 500 units, our wholesale tier provides a 38% volume discount with a standard 3-week production lead time. I have attached our complete product catalog and look forward to discussing how we can support your holiday rollout.\n\nWarm regards,\nMaya Chen\nNorthstar Studio',
      created: new Date().toISOString()
    },
    {
      id: 'inbox_2',
      from: 'markus@atelierdesign.io',
      subject: 'Custom collaboration proposal for Spring 2027',
      preview: 'Loved your recent feature in Design Digest. Would your artisans be open to a limited-edition collaboration?',
      body: 'Hello Northstar,\n\nWe love your material transparency and would love to propose a co-branded stoneware line for our spring showcase. Let us know if you have 15 minutes to connect next week.\n\nMarkus',
      status: 'triaged',
      triage_status: 'partnership',
      draft: 'Hi Markus,\n\nThanks for reaching out and for following our feature in Design Digest! We are always open to purposeful design collaborations that align with our mindful craftsmanship principles. Next Tuesday afternoon works well for an introductory conversation — let me know what time suits your schedule.\n\nBest,\nMaya',
      created: new Date().toISOString()
    }
  ],
  websitePages: [
    {
      id: 'page_home',
      title: 'Home',
      path: '/',
      status: 'published',
      blocks: [
        {
          id: 'blk_hero',
          name: 'Hero Headline',
          status: 'published',
          copy: 'Handcrafted Goods Designed for Modern Living.',
          source_refs: { messageId: 'msg_web_hero', planId: 'plan_1' }
        },
        {
          id: 'blk_val',
          name: 'Value Proposition',
          status: 'draft',
          copy: 'Every piece is sustainably sourced, crafted by independent artisans, and built to endure for generations.',
          source_refs: { messageId: 'msg_web_val', planId: null }
        }
      ]
    },
    {
      id: 'page_about',
      title: 'About Us',
      path: '/about',
      status: 'published',
      blocks: [
        {
          id: 'blk_story',
          name: 'Our Story',
          status: 'draft',
          copy: 'Founded with a commitment to mindful craftsmanship, transparent material sourcing, and zero-compromise design.',
          source_refs: null
        }
      ]
    }
  ],
  activity: [],
  adCampaignBriefs: [
    {
      id: 'brief_retention',
      plan: 'plan_1',
      title: 'Q3 Retention Push',
      status: 'approved',
      targetAudience: 'Existing customers (purchased in last 12 months)',
      keyMessage: 'Bring sustainable craftsmanship home: exclusive early access to autumn essentials.',
      variants: [
        'Variant A: "Crafted to last a lifetime. Discover our autumn collection early."',
        'Variant B: "Your home deserves mindful design. Exclusive early access inside."',
        'Variant C: "Sustainable luxury made by independent artisans. Welcome back."'
      ],
      created: new Date().toISOString()
    }
  ],
  usage: [],
  marketplace: [
    {
      id: 'template_finance_team',
      type: 'team',
      name: 'Finance Team',
      slug: 'finance-team',
      color: '#5b8def',
      description: 'Strategic finance, cash-flow timing, and revenue variance analysis.',
      roster: ['Elena', 'Noah', 'Priya'],
      rosterDetails: [
        { name: 'Elena', role: 'Finance Lead', isLead: true, description: 'Leads financial modeling and synthesizes specialist consultations.' },
        { name: 'Noah', role: 'Revenue Analyst', isLead: false, description: 'Analyzes revenue concentration and customer demand patterns.' },
        { name: 'Priya', role: 'Cash Flow Planner', isLead: false, description: 'Models runway, inflow timing, and practical operational tradeoffs.' }
      ]
    },
    {
      id: 'template_growth_team',
      type: 'team',
      name: 'Growth & Ads Team',
      slug: 'growth-team',
      color: '#ef806e',
      description: 'Evidence-led campaign strategy, audience definitions, and creative variants.',
      roster: ['Mateo', 'Zoe'],
      rosterDetails: [
        { name: 'Mateo', role: 'Growth Lead', isLead: true, description: 'Develops measurable campaign briefs and audience channel mix.' },
        { name: 'Zoe', role: 'Ad Copywriter', isLead: false, description: 'Drafts high-converting headline variants and messaging hooks.' }
      ]
    },
    {
      id: 'template_website_team',
      type: 'team',
      name: 'Website Architecture Team',
      slug: 'website-team',
      color: '#62c5a7',
      description: 'Conversion-aware structure, content review, and technical SEO discoverability.',
      roster: ['Iris', 'Finn'],
      rosterDetails: [
        { name: 'Iris', role: 'Web Strategist', isLead: true, description: 'Shapes page flow, content architecture, and conversion paths.' },
        { name: 'Finn', role: 'SEO Specialist', isLead: false, description: 'Optimizes on-page search intent coverage and information architecture.' }
      ]
    },
    {
      id: 'template_engineering_team',
      type: 'team',
      name: 'Engineering Team',
      slug: 'engineering-team',
      color: '#9979dc',
      description: 'Full-stack technical architecture, API design, and systems scalability.',
      roster: ['Devon', 'Kiran'],
      rosterDetails: [
        { name: 'Devon', role: 'Technical Lead', isLead: true, description: 'Directs systems architecture, reliability, and code review.' },
        { name: 'Kiran', role: 'Integration Specialist', isLead: false, description: 'Builds third-party API webhooks and data synchronization pipelines.' }
      ]
    },
    {
      id: 'template_seo_specialist',
      type: 'bot',
      name: 'SEO Specialist (Finn)',
      slug: 'seo-specialist',
      color: '#62c5a7',
      description: 'Standalone search discoverability specialist for on-page audits and keyword research.',
      roster: ['Finn'],
      rosterDetails: [
        { name: 'Finn', role: 'SEO Specialist', isLead: false, description: 'Improves discoverability and internal linking.' }
      ]
    }
  ],
  projects: [
    {
      id: 'proj_autumn_launch',
      name: 'Autumn Collection Launch',
      description: 'Coordinated rollout of new handcrafted ceramics collection across Website, Ads, and Retail.',
      status: 'active',
      client: 'Direct to Consumer',
      created: new Date().toISOString()
    },
    {
      id: 'proj_retail_expansion',
      name: 'Retail Expansion Q4',
      description: 'Wholesale partnership with regional retailers and fulfillment logistics.',
      status: 'active',
      client: 'Acme Partners & Regional Boutiques',
      created: new Date().toISOString()
    }
  ],
  installedTemplates: [],
  subscriptionPlans: []
};
